import { DefaultValue, selector, selectorFamily, } from 'recoil';
/**
 * Creates a Recoil selector that can be used to get and set data from a Recoil state.
 * This becomes particularly useful when your request to a service is stored in Recoil and you want to get or set a small piece of that state.
 * This helper makes creating such a selector a lot easier and reduces the amount of boilerplate code you need to write.
 * ## Example
 * Example on using createGetterSetterSelector for a request that needs to get and set the Limit property.
 * ```typescript
 * const limitSearchOrders = createGetterSetterSelector({
 *  key: 'OrdersList:Request:Limit',
 *  state: ordersListState.request,
 *  defaultValue: 10,
 *  getData: (request) => request?.PageConfig?.Limit,
 *  setData: (request, newValue) => ({
 *    ...request,
 *    PageConfig: { ...request?.PageConfig, Limit: newValue },
 *  }),
 *});
 * ```
 */
export const createGetterSetterSelector = ({ defaultValue, getData, key, setData, state, }) => selector({
    key,
    get: ({ get }) => {
        const currentState = get(state);
        return getData(currentState, get);
    },
    set: ({ get, set }, newValue) => {
        const currentState = get(state);
        const currentValue = getData(currentState, get);
        if (newValue instanceof DefaultValue && currentValue !== defaultValue) {
            const newState = setData(currentState, defaultValue, set, get);
            set(state, newState);
        }
        else if (!(newValue instanceof DefaultValue) &&
            currentValue !== newValue) {
            const newState = setData(currentState, newValue, set, get);
            set(state, newState);
        }
    },
});
/**
 * Creates a Recoil selectorFamily that can be used to get and set data from a Recoil state.
 * This becomes particularly useful when your request to a service is stored in Recoil and you want to get or set a small piece of that state.
 * This helper makes creating such a selectorFamily a lot easier and reduces the amount of boilerplate code you need to write.
 * Keep in mind, this will create a family of selectors.
 * ## Example
 * Example on using createGetterSetterSelectorFamily for a request that needs to get and set the OrderID property.
 * ```typescript
 * const orderShipmentsOrderIDRequestSelector = createGetterSetterSelectorFamily<
 *  EVA.Core.ListOrderShipments,
 *  number | undefined,
 *  number | undefined,
 * >({
 *    recoilKey: 'OrderShipments:Request:OrderID',
 *    state: orderShipmentsState.request,
 *    getData: (request) => request?.OrderID,
 *    setData: (request, newValue) => {
 *      if (newValue) {
 *        return {
 *          ...request,
 *          OrderID: newValue,
 *        };
 *      } else {
 *        return request;
 *      },
 *  });
 * ```
 */
export const createGetterSetterSelectorFamily = ({ defaultValue, getData, key, setData, state, }) => selectorFamily({
    key,
    get: (params) => {
        return ({ get }) => {
            const currentState = get(state(params));
            return getData(currentState, get, params);
        };
    },
    set: (params) => {
        return ({ get, set }, newValue) => {
            const currentState = get(state(params));
            const currentValue = getData(currentState, get);
            if (newValue instanceof DefaultValue && currentValue !== defaultValue) {
                const newState = setData(currentState, defaultValue, set, get, params);
                set(state(params), newState);
            }
            else if (!(newValue instanceof DefaultValue) &&
                currentValue !== newValue) {
                const newState = setData(currentState, newValue, set, get, params);
                set(state(params), newState);
            }
        };
    },
});
//# sourceMappingURL=create-selectors.js.map